import axios from "axios";
import { getApiUrlWithVersion } from "./environmentService";
import { getAuthUser } from "./authService";

export interface EmailTemplate {
  id: number;
  title: string;
  description: string | null;
  tags: string[];
  preview_html: string | null;
  lifecycle_preview_html: string | null;
}

const TAG_COLOR_MAP: Record<string, { variant: string; bgColor: string; textColor: string }> = {
  playful:  { variant: "playful",   bgColor: "#e8e8fe", textColor: "#5d5ded" },
  minimal:  { variant: "minimal",   bgColor: "#dbeafe", textColor: "#1e40af" },
  editorial:{ variant: "editorial", bgColor: "#dcfce7", textColor: "#166534" },
};

export function tagClassName(tag: string): string {
  const entry = TAG_COLOR_MAP[tag.toLowerCase()];
  return entry ? `template-card-tag template-card-tag--${entry.variant}` : "template-card-tag";
}

export function tagActiveStyle(tag: string): { backgroundColor: string; color: string } {
  const entry = TAG_COLOR_MAP[tag.toLowerCase()];
  return entry
    ? { backgroundColor: entry.bgColor, color: entry.textColor }
    : { backgroundColor: "#d1d5db", color: "#374151" };
}

export function displayTag(tag: string): string {
  return tag.charAt(0).toUpperCase() + tag.slice(1);
}

export interface TemplateWithImportStatus {
  id: number;
  title: string;
  tags: string[];
  already_imported: boolean;
}

export interface Blueprint {
  id: number;
  title: string;
  audience: string | null;
  has_lifecycle_sibling: boolean;
  has_default_template: boolean;
  preview_html: string | null;
  blueprint_type: "lifecycle" | "newsletter";
}

export interface ImportTemplateResponse {
  newsletter_blueprint_id: number;
  lifecycle_blueprint_id: number;
  newsletter_edition_id: number;
  lifecycle_edition_id: number;
}

export interface BuildEditionResponse {
  edition_id: number;
  title: string;
  status: string;
}

export class EmailTemplatesService {
  static async getTemplates(): Promise<EmailTemplate[]> {
    const response = await axios.get(`${getApiUrlWithVersion()}/email_templates`);
    return response.data;
  }

  static async getTemplate(id: number): Promise<EmailTemplate> {
    const response = await axios.get(`${getApiUrlWithVersion()}/email_templates/${id}`);
    return response.data;
  }

  static async getTemplatesWithImportStatus(): Promise<TemplateWithImportStatus[]> {
    const response = await axios.get(`${getApiUrlWithVersion()}/lifecycle_emails/templates`, {
      headers: { Authorization: getAuthUser()?.accessToken },
    });
    return response.data;
  }

  static async checkImportStatus(templateId: number): Promise<boolean> {
    const templates = await EmailTemplatesService.getTemplatesWithImportStatus();
    const match = templates.find((t) => t.id === templateId);
    return match?.already_imported ?? false;
  }

  static async importTemplate(templateId: number, name?: string): Promise<ImportTemplateResponse> {
    const response = await axios.post(
      `${getApiUrlWithVersion()}/lifecycle_emails/import_template`,
      { template_id: templateId, ...(name ? { name } : {}) },
      { headers: { Authorization: getAuthUser()?.accessToken } }
    );
    return response.data;
  }

  static async getBlueprints(): Promise<Blueprint[]> {
    const response = await axios.get(`${getApiUrlWithVersion()}/lifecycle_emails/blueprints`, {
      headers: { Authorization: getAuthUser()?.accessToken },
    });
    return response.data;
  }

  static async buildEdition(blueprintId: number): Promise<BuildEditionResponse> {
    const response = await axios.post(
      `${getApiUrlWithVersion()}/lifecycle_emails/build_edition`,
      { blueprint_id: blueprintId },
      { headers: { Authorization: getAuthUser()?.accessToken } }
    );
    return response.data;
  }
}
